import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { themes } from "services/constants";

export const ActivePeriod = ({ userId, onClose }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) {
      setPeriods([]);
      setError("No user selected.");
      return;
    }

    const fetchPeriods = async () => {
      setLoading(true);
      setError("");
      try {
        const url = `${domain}get-employment-periods/?user_id=${userId}`;
        const response = await customGetAPI(url);
        if (response.ok) {
          const data = response.responseDetails;
          let list = [];
          if (Array.isArray(data.employment_periods)) {
            list = data.employment_periods;
          }
          setPeriods(list);
          if (list.length === 0) {
            setError("No employment periods found.");
          }
        } else {
          setPeriods([]);
          setError(response.message || "Unable to load employment periods.");
        }
      } catch (err) {
        setPeriods([]);
        setError(err.message || "Unable to load employment periods.");
      } finally {
        setLoading(false);
      }
    };

    fetchPeriods();
  }, [domain, userId]);

  const columns = useMemo(() => {
    if (!periods?.length) return [];
    return Object.keys(periods[0] || {}).filter(
      (k) => k.toLowerCase() !== "id",
    );
  }, [periods]);

  const renderCellValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value;
  };

  const formatHeader = (header) =>
    header.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <Mui.Box sx={{ minHeight: 240,paddingTop:1 }}>
      {loading ? (
        <Loader info="Loading employment periods..." />
      ) : error ? (
        <Mui.Typography color="error">{error}</Mui.Typography>
      ) : periods.length === 0 ? (
        <Mui.Box sx={{pading: 2, display: "flex", justifyContent: "center" }}>
          <Mui.Typography>No employment periods found.</Mui.Typography>
        </Mui.Box>
      ) : (
        <Mui.TableContainer
          component={Mui.Paper}
          sx={{
            width: "100%",
            maxHeight: { md: 710, lg: 710, xl: 710 },
            ml: 1,
            mr: 0.5,
            backgroundColor: themes?.whiteColor,
          }}
        >
          <Mui.Table stickyHeader>
            <Mui.TableHead
              sx={{
                backgroundColor: themes.primary,
                position: "sticky",
                top: -1,
                zIndex: 10,
              }}
            >
              <Mui.TableRow>
                {columns.map((column) => (
                  <Mui.TableCell
                    key={column}
                    sx={{
                      color: themes.headLine,
                      fontWeight: "bold",
                      backgroundColor: themes.primary,
                      padding: "6px 35px",
                    }}
                  >
                    {formatHeader(column)}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {periods.map((row, rowIndex) => (
                <Mui.TableRow
                  key={rowIndex}
                  hover
                  sx={{
                    "& .MuiTableCell-root": {
                      height: "15px",
                      padding: "6px 35px",
                    },
                  }}
                >
                  {columns.map((column) => (
                    <Mui.TableCell key={`${rowIndex}-${column}`}>
                      {renderCellValue(row[column])}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
      )}
    </Mui.Box>
  );
};
